import type {
  ErrorCode,
  GameId,
  MeResponse,
  ServerMessage,
  SettlementReceipt,
  StakeTier,
  TableSnapshot,
} from '@tira/protocol';
import { create } from 'zustand';
import { api, setToken } from '../net/api.js';
import { socket, type SocketStatus } from '../net/socket.js';

/**
 * Single app store. The server is the bahay (design spec §4 pillar 4) — this
 * holds only what the server has told us, plus local navigation. Nothing here
 * computes a game outcome or a chip balance.
 */

export type Route =
  | { s: 'splash' }
  | { s: 'agegate' }
  | { s: 'compound' }
  | { s: 'venue'; game: GameId }
  | { s: 'table'; tableId: string }
  | { s: 'results'; matchId: string }
  | { s: 'store' }
  | { s: 'wallet' }
  | { s: 'settings' }
  | { s: 'kubo' }
  | { s: 'fairplay'; matchId: string };

export interface QueueState {
  game: GameId;
  tier: StakeTier;
  position: number;
  etaMs: number;
}

export interface MatchState {
  matchId: string;
  tableId: string;
  game: GameId;
  seat: number;
  /** Whatever the engine's per-seat projection is. Screens narrow this. */
  view: unknown;
  seq: number;
  turn: number | null;
  deadline: number | null;
  turnMs: number;
  commit: string;
  /** Newest last. Drives the table-talk float and event log. */
  log: { seq: number; event: unknown }[];
}

interface AppState {
  route: Route;
  me: MeResponse | null;
  balance: number;
  status: SocketStatus;
  queue: QueueState | null;
  table: TableSnapshot | null;
  match: MatchState | null;
  receipt: SettlementReceipt | null;
  /**
   * Set between settlement and Results, while the board shows how the hand
   * ended. The receipt is already in hand; this is only about who owns the
   * screen.
   */
  settling: { matchId: string; until: number } | null;
  /** Transient toast — errors say what happened and what to do next (§12). */
  toast: { text: string; tone: 'info' | 'warn' | 'win'; code?: ErrorCode } | null;
  talk: { seat: number; preset: string; at: number }[];
  rematch: { ready: number[]; needed: number } | null;

  go(r: Route): void;
  /** Leave the finished board for the receipt — on the timer, or on a tap. */
  showReceipt(): void;
  boot(): Promise<void>;
  acceptAgeGate(): Promise<void>;
  refreshMe(): Promise<void>;
  /** `code` lets Toast say it in the player's language; `text` is the fallback. */
  setToast(text: string, tone?: 'info' | 'warn' | 'win', code?: ErrorCode): void;
  handle(m: ServerMessage): void;
}

/**
 * How long the finished board keeps the screen. Long enough for a tile cascade
 * or a bilangan to read, short enough that a player who does not care is not
 * held — and collapsed under reduced motion, where those flourishes are fades
 * anyway (design spec §14).
 */
function dwellMs(): number {
  if (typeof document === 'undefined') return 0;
  return document.documentElement.getAttribute('data-motion') === 'reduced' ? 900 : 3_200;
}

export const useApp = create<AppState>((set, get) => ({
  route: { s: 'splash' },
  me: null,
  balance: 0,
  status: 'idle',
  queue: null,
  table: null,
  match: null,
  receipt: null,
  settling: null,
  toast: null,
  talk: [],
  rematch: null,

  go: (route) => set({ route, settling: null }),

  showReceipt() {
    const { settling, receipt } = get();
    const matchId = settling?.matchId ?? receipt?.matchId;
    if (!matchId) return;
    set({ settling: null, route: { s: 'results', matchId } });
  },

  async boot() {
    let me: MeResponse;
    try {
      me = await api.me();
    } catch {
      // First run, or a token we can no longer use: mint a guest account.
      const auth = await api.guest();
      setToken(auth.token);
      me = auth.user;
    }
    set({ me, balance: me.balance });
    const token = localStorage.getItem('tpm.token');
    if (token) socket.connect(token);
    set({ route: me.ageVerified ? { s: 'compound' } : { s: 'agegate' } });
  },

  async acceptAgeGate() {
    const me = await api.acceptAgeGate();
    set({ me, balance: me.balance, route: { s: 'compound' } });
  },

  async refreshMe() {
    const me = await api.me();
    set({ me, balance: me.balance });
  },

  setToast(text, tone = 'info', code) {
    set({ toast: { text, tone, code } });
    setTimeout(() => {
      if (get().toast?.text === text) set({ toast: null });
    }, 3200);
  },

  handle(m) {
    switch (m.t) {
      case 'hello.ok':
        set({ balance: m.balance });
        break;
      case 'queue.update':
        set({ queue: { game: m.game, tier: m.tier, position: m.position, etaMs: m.etaMs } });
        break;
      case 'queue.left':
        set({ queue: null });
        break;
      case 'table.state':
        set({ table: m.table });
        break;
      case 'match.start':
        set({
          queue: null,
          receipt: null,
          // The next hand outranks the last one's dwell. Left set, the previous
          // timer still fires — three seconds into a rematch it yanked the
          // player out of a live hand and into the old receipt.
          settling: null,
          rematch: null,
          talk: [],
          route: { s: 'table', tableId: m.tableId },
          match: {
            matchId: m.matchId,
            tableId: m.tableId,
            game: m.game,
            seat: m.seat,
            view: m.view,
            // Start from the table's seq at deal time. Starting at 0 made the
            // first action of every match fail as stale.
            seq: m.seq,
            // Not null: the opening move has a seat on the clock and a timer
            // already running, and pretending otherwise left the first ply of
            // every match with no ring and no countdown.
            turn: m.turn,
            deadline: m.deadline,
            turnMs: m.turnMs,
            commit: m.commit,
            log: [],
          },
        });
        break;
      case 'match.event': {
        const cur = get().match;
        if (!cur || cur.matchId !== m.matchId || m.seq <= cur.seq) break;
        set({
          match: {
            ...cur,
            view: m.view,
            seq: m.seq,
            turn: m.turn,
            deadline: m.deadline,
            log: [...cur.log.slice(-60), { seq: m.seq, event: m.event }],
          },
        });
        break;
      }
      /*
       * Settlement arrives within a database round-trip of the final frame —
       * tens of milliseconds — because the server settles in the same tick the
       * engine sets `over`. Flipping straight to Results therefore unmounted
       * every venue's end-of-hand moment before it could play: the Tongits
       * bilangan, the Todas slam, the Pusoy reveal cascade, the Gera verdict.
       * All of them were built, and none of them had ever been seen.
       *
       * So the receipt lands now — the chips have moved and the balance must be
       * honest — but the table keeps the screen for a beat first. Tapping the
       * board skips it; `showReceipt` is the only way to Results either way.
       */
      case 'match.settle': {
        set({ receipt: m.receipt, balance: m.balance });
        if (get().route.s !== 'table') {
          set({ route: { s: 'results', matchId: m.matchId } });
          break;
        }
        set({ settling: { matchId: m.matchId, until: Date.now() + dwellMs() } });
        setTimeout(() => {
          if (get().settling?.matchId === m.matchId) get().showReceipt();
        }, dwellMs());
        break;
      }
      /*
       * A seat dropped or came back. The snapshot that follows carries the same
       * fact, but this arrives first and costs nothing — and while the grace
       * runs the server auto-plays that seat, so the sooner the puck goes grey
       * the sooner the table stops looking like someone playing badly on purpose.
       */
      case 'presence': {
        const table = get().table;
        if (!table || table.tableId !== m.tableId) break;
        set({
          table: {
            ...table,
            seats: table.seats.map((s) =>
              s && s.seat === m.seat ? { ...s, connected: m.connected, ghostUntil: m.ghostUntil } : s,
            ),
          },
        });
        break;
      }
      case 'wallet.update':
        set({ balance: m.balance });
        break;
      case 'table.talk':
        set({ talk: [...get().talk.slice(-8), { seat: m.seat, preset: m.preset, at: Date.now() }] });
        break;
      case 'rematch.state':
        set({ rematch: { ready: m.ready, needed: m.needed } });
        break;
      case 'error':
        get().setToast(m.message, 'warn', m.code);
        break;
      default:
        break;
    }
  },
}));

socket.on((m) => useApp.getState().handle(m));
socket.onStatus((status) => useApp.setState({ status }));
